'use client';

import LinkRouter from 'next/link';
import React from 'react';

import {
    Alignable,
    Column,
    Columns,
    Icon,
    IconColor,
    IconName,
    Link,
    Row,
    Rows,
    Sticker,
    Text,
    TextLevels,
    TextMarkup,
    TypographyBold,
} from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ProductChildFull } from '@app-types/product';

import { getSimilarProductsLink } from '@helpers/product';

import { isOutOfStock } from '@product-detail/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

export type StockProps = {
    type: WallType;
    currentProduct: ProductChildFull;
    openModal: ClickEvent;
    isRenewal: boolean;
};

export function Stock({ type, currentProduct, openModal, isRenewal }: StockProps) {
    const {
        isPreresa,
        stock,
        hasClickAndCollect,
        hasFreeShipping,
        isAvailableInStore,
        hasStockInStore,
        closestShippingDate,
    } = currentProduct.stockDetails;

    const isPhone = type === WallType.PHONE;

    return (
        <Row>
            <Rows gap={2}>
                <Row>
                    <Columns
                        className='gap-medium'
                        marginless
                        verticalAlign={Alignable.ALIGNED_CENTER}
                        mobile
                        data-cy={`stock-${stock.status}`}
                    >
                        <Column narrow className='is-flex is-paddingless'>
                            <Icon name={stock.altIcon} color={stock.color} />
                        </Column>
                        <Column>
                            <Text
                                markup={TextMarkup.SPAN}
                                typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                                level={TextLevels.TWO}
                                marginless
                            >
                                {stock.label}
                            </Text>
                            {isOutOfStock(stock.status) ? (
                                <Link href={getSimilarProductsLink(currentProduct, isRenewal)} routerLink={LinkRouter}>
                                    &nbsp;{stock.subLabel}
                                </Link>
                            ) : (
                                <Text markup={TextMarkup.SPAN} level={TextLevels.TWO} marginless>
                                    &nbsp;{stock.subLabel}
                                    {closestShippingDate && ` ${closestShippingDate}`}
                                </Text>
                            )}
                        </Column>
                    </Columns>
                </Row>
                {isPhone && hasClickAndCollect && (
                    <Availability text='Disponible en Click&Collect' subText=' avec retrait gratuit dès 2h' />
                )}
                {isPhone && hasFreeShipping && (
                    <Row>
                        <Sticker
                            className='has-background-secondary'
                            small
                            iconName={IconName.TRUCK_SPEED}
                            label={`\u00A0Livraison offerte`}
                        />
                    </Row>
                )}
                {isPhone && isAvailableInStore && <Availability text='Disponible en Boutique' />}

                {isPhone && hasStockInStore && (
                    <Row>
                        <Text level={TextLevels.TWO}>
                            <Link role='button' onClick={openModal}>
                                Voir les disponibilités en boutique
                            </Link>
                        </Text>
                    </Row>
                )}
                {isPhone && isPreresa && (
                    <Row>
                        <Text level={TextLevels.TWO}>
                            <Link role='button' onClick={openModal}>
                                Préréserver en boutique
                            </Link>
                        </Text>
                    </Row>
                )}
            </Rows>
        </Row>
    );
}

function Availability({ text, subText = '' }: { text: string; subText?: string }) {
    return (
        <Row>
            <Columns className='gap-medium' marginless verticalAlign={Alignable.ALIGNED_CENTER} mobile>
                <Column narrow className='is-flex is-paddingless'>
                    <Icon name={IconName.CHECK_CIRCLE} color={IconColor.SUCCESS} />
                </Column>
                <Column>
                    <Text
                        markup={TextMarkup.SPAN}
                        typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                        level={TextLevels.TWO}
                        marginless
                    >
                        {text}
                    </Text>
                    <Text markup={TextMarkup.SPAN} level={TextLevels.TWO} marginless>
                        &nbsp;{subText}
                    </Text>
                </Column>
            </Columns>
        </Row>
    );
}
